import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { GridApi, ColDef, CellValueChangedEvent } from "ag-grid-community";
import Grid from "../../Components/Grid/Grid";
import config from "../../config";
import Button from "../../Components/button/button";

interface EverythingData {
  id: number;
  customer: string;
  network: string;
  site: string;
  link: string;
  asset: string;
  antenna: string;
}

const Allupdate: React.FC = () => {
  const [rowData, setRowData] = useState<
    (EverythingData & { tempId?: number })[]
  >([]);
  const gridApiRef = useRef<GridApi<any> | null>(null);
  const tempIdRef = useRef(-1); // For new rows

  // Column definitions with editable cells
  const columnDefs: ColDef<EverythingData>[] = [
    {
      headerName: "Customer",
      field: "customer",
      sortable: true,
      filter: true,
      flex: 1,
      editable: true,
    },
    {
      headerName: "Network",
      field: "network",
      sortable: true,
      filter: true,
      flex: 1,
      editable: true,
    },
    {
      headerName: "Site",
      field: "site",
      sortable: true,
      filter: true,
      flex: 1,
      editable: true,
    },
    {
      headerName: "Link",
      field: "link",
      sortable: true,
      filter: true,
      flex: 1,
      editable: true,
    },
    {
      headerName: "Asset",
      field: "asset",
      sortable: true,
      filter: true,
      flex: 1,
      editable: true,
    },
    {
      headerName: "Antenna",
      field: "antenna",
      sortable: true,
      filter: true,
      flex: 1,
      editable: true,
    },
  ];

  // Fetch everything data from backend
  useEffect(() => {
    axios
      .get<EverythingData[]>(`${config.apiBaseUrl}/api/everything/all`)
      .then((res) => setRowData(res.data))
      .catch((err) => console.error("Error fetching Everything data:", err));
  }, []);

  // Handle editing existing rows
  const handleCellValueChanged = (
    event: CellValueChangedEvent<EverythingData & { tempId?: number }>,
  ) => {
    const updatedRow = event.data;

    if (updatedRow.id !== 0 && updatedRow.id !== undefined) {
      // Existing row, update via PUT
      axios
        .put(`${config.apiBaseUrl}/api/everything/${updatedRow.id}`, updatedRow)
        .then(() => console.log("Row updated successfully", updatedRow))
        .catch((err) => console.error("Error updating row:", err));
    }
  };

  // Handle new row creation or updates
  const handleNewRowSave = (
    event: CellValueChangedEvent<EverythingData & { tempId?: number }>,
  ) => {
    const row = event.data;

    // If it's a new row (tempId exists)
    if (row.id === 0 && row.tempId !== undefined) {
      axios
        .post(`${config.apiBaseUrl}/api/everything`, row)
        .then((res) => {
          // Replace temporary row with backend response (with real ID)
          setRowData((prev) =>
            prev.map((r) => (r.tempId === row.tempId ? res.data : r)),
          );
        })
        .catch((err) => console.error("Error saving new row:", err));
    } else {
      handleCellValueChanged(event); // Existing row
    }
  };

  // Add new row on top
  const handleAddRow = () => {
    const newRow: EverythingData & { tempId: number } = {
      tempId: tempIdRef.current--, // Unique temporary ID
      id: 0, // Will be replaced by backend
      customer: "",
      network: "",
      site: "",
      link: "",
      asset: "",
      antenna: "",
    };

    setRowData((prev) => [newRow, ...prev]);

    // Focus the first cell of the new row
    setTimeout(() => {
      if (gridApiRef.current) {
        const firstRowNode = gridApiRef.current.getDisplayedRowAtIndex(0);
        if (firstRowNode && firstRowNode.rowIndex !== null) {
          const rowIndex = firstRowNode.rowIndex; // safely store as number
          gridApiRef.current.setFocusedCell(rowIndex, "customer");
          gridApiRef.current.startEditingCell({
            rowIndex: rowIndex,
            colKey: "customer",
          });
        }
      }
    }, 100);
  };

  return (
    <div style={{ height: "700px", width: "100%" }}>
      <Button onClick={handleAddRow}>+ Add Row</Button>
      <Grid<EverythingData & { tempId?: number }>
        rowData={rowData}
        columnDefs={columnDefs}
        getRowId={(params) => String(params.data.id || params.data.tempId)} // Important for AG Grid to track new rows
        onCellValueChanged={handleNewRowSave}
        onGridReady={(params: { api: GridApi<any> | null }) => {
          gridApiRef.current = params.api;
        }}
      />
    </div>
  );
};

export default Allupdate;
